// Phase 5 catalog cutover (docs/sdk-refactor/phases/phase-5-catalog-cutover.md):
// display reads come from treatink-api /v1/catalog/*, adapted to the exact legacy
// product shape (dual PascalCase/lowercase keys) so no consumer changes.
//
// RULE L4: `id`/`ID` stays the SUPABASE product id (via catalog-map) — cart items keep
// Supabase identity because create-stripe-intent re-prices against Supabase rows.
// Pricing parity between the two catalogs is enforced by scripts/catalog-parity.mjs.
//
// NOTE: catalog_image.url is a SHORT-LIVED signed URL — responses must not be cached
// long or persisted (apiSlice sets keepUnusedDataFor accordingly; `api` reducer is not
// in the redux-persist whitelist).

import { TREATINK_API_BASE, TREATINK_PK } from "../config/endpoints";
import catalogMap from "../config/catalog-map.json";

const skuByVariantId = Object.fromEntries(
  Object.entries(catalogMap.bySku).map(([sku, m]) => [m.variantId, sku]),
);
const sbBundleIdByApiId = Object.fromEntries(
  Object.entries(catalogMap.bundles ?? {}).map(([sbId, b]) => [b.apiBundleId, Number(sbId)]),
);

async function tkGet(path) {
  const res = await fetch(`${TREATINK_API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${TREATINK_PK}` },
  });
  if (!res.ok) throw new Error(`${path} -> HTTP ${res.status}`);
  return res.json();
}

async function tkGetAll(path) {
  const out = [];
  let cursor;
  do {
    const page = await tkGet(`${path}?limit=100${cursor ? `&cursor=${cursor}` : ""}`);
    out.push(...page.data);
    cursor = page.has_more ? page.next_cursor : undefined;
  } while (cursor);
  return out;
}

/** variant + its product family → the legacy dual-case product object. */
export function adaptProduct(variant, family) {
  const mapping = catalogMap.bySku[variant.sku];
  if (!mapping) return null; // unmapped SKU → not sellable through the store (L4)
  // Price comes from the parity-verified map, not the live API value — so a unilateral
  // API price change can never make the displayed price drift from what Stripe charges
  // (create-stripe-intent re-prices against Supabase). Drift shows up in the parity
  // script, not in customers' carts.
  const price = mapping.priceCents / 100;
  const flavor = variant.option_values?.flavor ?? null;
  const image = variant.catalog_image?.url ?? null;
  const label = variant.regulatory_label_image?.url ?? null;
  const petType = family?.animal_type ?? null;
  const base = {
    id: mapping.supabaseId,
    title: family?.title ?? variant.sku,
    sku: variant.sku,
    price,
    flavor,
    image,
    label,
    petType,
    category: family?.category ?? null,
    type: family?.product_type ?? null,
    description: variant.description ?? family?.description ?? null,
    shortDescription: variant.short_description ?? null,
  };
  return {
    ...base,
    ID: base.id,
    Title: base.title,
    SKU: base.sku,
    Price: base.price,
    Flavor: base.flavor,
    Image: base.image,
    Label: base.label,
    Animal: base.petType,
    Category: base.category,
    Type: base.type,
    Description: base.description,
    ShortDescription: base.shortDescription,
    Status: "published",
  };
}

export function adaptBundle(bundle, adaptedBySku) {
  const sbId = sbBundleIdByApiId[bundle.id];
  if (sbId === undefined) return null;
  const products = bundle.variant_ids
    .map((variantId) => adaptedBySku[skuByVariantId[variantId]])
    .filter(Boolean);
  if (products.length !== bundle.variant_ids.length) return null;
  return {
    id: sbId,
    name: bundle.name,
    description: bundle.description ?? "",
    productIds: products.map((p) => p.id),
    discountAmount: bundle.discount_cents / 100,
    // BundleBox and cart pricing hard-depend on these three (legacy fn shape):
    rawTotal: bundle.raw_total_cents / 100,
    total: bundle.suggested_retail_cents / 100,
    saved: bundle.discount_cents / 100,
    petType: bundle.animal_type ?? null,
    products,
  };
}

export async function fetchTkProducts() {
  const [variants, families] = await Promise.all([
    tkGetAll("/v1/catalog/variants"),
    tkGetAll("/v1/catalog/products"),
  ]);
  const familyById = new Map(families.map((f) => [f.id, f]));
  return variants
    .filter((v) => v.availability === "available")
    .map((v) => adaptProduct(v, familyById.get(v.product_id)))
    .filter(Boolean);
}

export async function fetchTkBundles() {
  const [products, bundles] = await Promise.all([fetchTkProducts(), tkGetAll("/v1/catalog/bundles")]);
  const adaptedBySku = Object.fromEntries(products.map((p) => [p.sku, p]));
  return bundles.map((b) => adaptBundle(b, adaptedBySku)).filter(Boolean);
}
